import {
  Recipe,
  RecipeGroupId,
  RecipeLegendItem,
  RecipeOwnerType,
} from './recipes';

export type AvailableViewOwnerType = RecipeOwnerType;
export type AvailableViewGroupId = RecipeGroupId;

export type AvailableViewSummary = Recipe & {
  readonly componentCount: number;
  readonly componentLabels: readonly string[];
};

export interface AvailableViewsResponseBody {
  readonly views: readonly AvailableViewSummary[];
}

export interface AvailableViewGroupMeta {
  readonly id: AvailableViewGroupId;
  readonly title: string;
  readonly description: string;
  readonly emptyTitle: string;
  readonly emptyBody: string;
}

export const AVAILABLE_VIEW_GROUPS: readonly AvailableViewGroupMeta[] = [
  {
    id: 'ptsStandard',
    title: 'XAIS-PTS',
    description:
      '',
      // 'XAIS-PTS defaults: single-parameter layers and multi-parameter recipes with one control.',
    emptyTitle: 'No built-in layers yet',
    emptyBody:
      'XAIS-PTS recommended layers and recipes will appear here once they are assigned to this project.',
  },
  {
    id: 'clientStandard',
    title: 'Client recipes',
    description: 'Recipes commissioned for this authority.',
    emptyTitle: 'No client recipes',
    emptyBody: 'Client-defined recipes will appear here when assigned.',
  },
  {
    id: 'myLayers',
    title: 'My Layers',
    description: 'Layers you created in Layer builder.',
    emptyTitle: 'No layers yet',
    emptyBody: 'Open Layer builder to add a single parameter to the map.',
  },
] as const;

export function formatOrganisationRecipesTitle(
  organisationName: string | null | undefined,
): string {
  const trimmed = organisationName?.trim();
  return trimmed ? `${trimmed} recipes` : 'Client recipes';
}

export function formatOrganisationRecipesEmptyTitle(
  organisationName: string | null | undefined,
): string {
  const trimmed = organisationName?.trim();
  return trimmed ? `No ${trimmed} recipes` : 'No client recipes';
}

export function resolveAvailableViewGroups(
  organisationName?: string | null,
): readonly AvailableViewGroupMeta[] {
  return AVAILABLE_VIEW_GROUPS.map((group) => {
    if (group.id !== 'clientStandard') {
      return group;
    }
    const trimmed = organisationName?.trim();
    return {
      ...group,
      title: formatOrganisationRecipesTitle(organisationName),
      emptyTitle: formatOrganisationRecipesEmptyTitle(organisationName),
      description: trimmed
        ? `Recipes commissioned for ${trimmed}.`
        : group.description,
      emptyBody: trimmed
        ? `${trimmed}-defined recipes will appear here when assigned.`
        : group.emptyBody,
    };
  });
}

export function ownerTypeBadgeLabel(
  ownerType: AvailableViewOwnerType,
  organisationName?: string | null,
): string {
  switch (ownerType) {
    case 'PTS':
      return 'XAIS-PTS';
    case 'CLIENT': {
      const trimmed = organisationName?.trim();
      return trimmed || 'Client';
    }
    case 'USER':
      return 'My layer';
    default: {
      const unreachable: never = ownerType;
      return unreachable;
    }
  }
}

export function toAvailableViewSummary(recipe: Recipe): AvailableViewSummary {
  const ordered = [...recipe.components].sort(
    (left, right) => left.displayOrder - right.displayOrder,
  );
  const legendItems =
    recipe.legendItems && recipe.legendItems.length > 0
      ? recipe.legendItems
      : (recipe.legendColors ?? []).map((color) => ({ color, label: color }));
  return {
    ...recipe,
    legendItems,
    legendColors:
      recipe.legendColors && recipe.legendColors.length > 0
        ? recipe.legendColors
        : legendItems.map((item) => item.color),
    legendThresholdAttribute: recipe.legendThresholdAttribute ?? null,
    componentCount: ordered.length,
    componentLabels: ordered.map((component) => component.label),
  };
}

export function resolveViewLegendItems(
  view: Pick<AvailableViewSummary, 'legendItems' | 'legendColors'>,
): readonly RecipeLegendItem[] {
  if (view.legendItems && view.legendItems.length > 0) {
    return view.legendItems;
  }
  return (view.legendColors ?? []).map((color) => ({ color, label: color }));
}

export function resolveViewThresholdAttribute(
  view: Pick<AvailableViewSummary, 'id' | 'legendThresholdAttribute'>,
): string | null {
  const explicit = view.legendThresholdAttribute?.trim();
  if (explicit) {
    return explicit;
  }
  const id = view.id;
  if (id.includes('pas2161')) {
    return 'Severity';
  }
  if (id === 'pts-mfv-cracking' || id.endsWith('-cracking') || id.includes('crackmap')) {
    return 'Width';
  }
  if (id === 'pts-mfv-potholes' || id.includes('pothole')) {
    return 'Ave Depth';
  }
  if (id === 'pts-mfv-line-condition' || id.includes('line-condition')) {
    return 'Condition';
  }
  return null;
}

export function formatThresholdValuesHeading(
  view: Pick<AvailableViewSummary, 'id' | 'legendThresholdAttribute'>,
): string {
  const attribute = resolveViewThresholdAttribute(view);
  return attribute ? `Threshold Values (${attribute})` : 'Threshold Values';
}
